import { propOr, pathOr } from 'ramda';

/* Core Dependencies */
import { useEffect } from 'react';
import { BrowserRouter, Route, Switch } from 'react-router-dom';
import { ScrollToTopOnPathChange } from 'components/ScrollToTopButton';

/* i18n Dependencies */
import { useIntl } from 'react-intl';

import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enUS, esES } from '@mui/x-date-pickers/locales';
import dateFnsAdapterEsLocale from 'date-fns/locale/es';
/* UI Dependencies */
import { ThemeProvider } from '@mui/material/styles';
import Layout from 'components/layout';
import theme from 'styles/ui-theme';

/* API */
import { useAuth0 } from '@auth0/auth0-react';
import { useQuery } from '@tanstack/react-query';
import apiRequest, { ApiError } from 'lib/API/wrapper';

/* Analytics */
import { trackEvent } from 'lib/analytics';
import TimeMe from 'timeme.js';
import { deviceDetect } from 'react-device-detect';

/* Redux */
import { useDispatch, useSelector } from 'react-redux';
import { setCurrentSoc } from 'app/slices/jobsSlice';
import { setExperienceForm, setExperienceHasResume } from 'app/slices/experienceSlice';
import { setActivities, setGuid, setStatus, setLoaded } from 'app/slices/workSearchSlice';
import {
  setLoading,
  setNeedsContactInfo,
  setNeedsOnboarding,
  setName,
  setPreferredName,
  setUserSettings,
  setOnboardingStep,
} from 'app/slices/appSlice';

/* Pages */
import { About, LaunchHP, Login, TermsOfUse, TermsOfUseES, CreditsCitations } from 'components/pages/unauthenticated';
import OnboardingPagesSwitch from 'components/pages/onboarding/OnboardingPagesSwitch';
import AuthenticatedPagesSwitch from 'components/pages/authenticated/AuthenticatedPagesSwitch';

/* UI Components */
import Loading from 'components/Loading';
import ErrorMessage from 'components/ErrorMessage';

import useAutoLogout from 'lib/hooks/useAutoLogout';
import { useSnackbar } from 'notistack';
import { configureAuthNew } from 'lib/API';

import { UI_COMPONENTS_UNDER_MAINTENANCE } from 'constants/featureFlags';

const englishLocale = enUS.components.MuiLocalizationProvider.defaultProps.localeText;
const spanishLocale = esES.components.MuiLocalizationProvider.defaultProps.localeText;

const REFRESH_TOKEN_INTERVAL_MS = 30000; // 30 seconds

// Helper function to check if user is currently on an onboarding page
const isOnOnboardingPage = () => window.location.pathname.includes('/onboarding/');

function App() {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  useAutoLogout();

  const { getAccessTokenSilently, isAuthenticated, isLoading: auth0_loading, logout, user: auth0_user } = useAuth0();

  const { loading, needsContactInfo, needsOnboarding, unrecoverableError } = useSelector((state) => state.app);
  const { loaded: worksearchLoaded } = useSelector((state) => state.worksearch);

  // eslint-disable-next-line
  const { options, ...dateFnsAdapterEsLocaleNoOptions } = dateFnsAdapterEsLocale;

  const signOut = () => {
    dispatch(setLoading(true));
    logout({
      returnTo: window.location.origin,
      federated: true,
    });
    dispatch(setLoading(false));
  };

  const getAccessToken = async () => {
    try {
      return await getAccessTokenSilently();
    } catch (e) {
      return null;
    }
  };

  useEffect(() => {
    // Function to call configureAuthNew initially
    configureAuthNew(getAccessToken);

    // Refresh access token silently
    const refreshAuth = async () => {
      try {
        await getAccessTokenSilently();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error refreshing access token:', error);
      }
    };

    let interval = setInterval(refreshAuth, REFRESH_TOKEN_INTERVAL_MS);

    if (!auth0_loading && isAuthenticated) {
      trackEvent('LOAD', {
        device: deviceDetect(),
        referrer: document.referrer,
        path: window.location.hash,
        language: navigator.language || navigator.userLanguage,
        localtime: Date(),
        username: auth0_user.sub,
      });
    } else {
      if (!auth0_loading) {
        dispatch(setLoading(false));
      }
    }

    // Cleanup function to clear interval
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [auth0_loading, isAuthenticated, getAccessToken]);

  const idQuery = useQuery({
    queryKey: ['/users/me'],
    enabled: isAuthenticated,
    retry: false,
    retryOnMount: false,
    refetchOnWindowFocus: false,
  });
  const additionalRequests = isAuthenticated && idQuery.isSuccess;

  const experienceQuery = useQuery({
    queryKey: ['/users/me/experience/'],
    enabled: additionalRequests,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const settingsQuery = useQuery({
    queryKey: ['/users/me/settings'],
    enabled: additionalRequests,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const locationQuery = useQuery({
    queryKey: ['/users/me/location/'],
    enabled: additionalRequests,
    retry: false,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    // Check if user has contact info (first_name and email are collected in OnboardingContact)
    const has_first_name = idQuery.data?.first_name && idQuery.data.first_name !== '';
    const has_email = idQuery.data?.email && idQuery.data.email !== '';
    const has_contact_info = has_first_name && has_email;

    if (
      idQuery.isSuccess &&
      !experienceQuery.isLoading &&
      (experienceQuery.isSuccess || experienceQuery.isError) &&
      settingsQuery.isSuccess &&
      (locationQuery.isSuccess || locationQuery.isError)
    ) {
      if (window.dataLayer && idQuery.data?.guid) {
        window.dataLayer.push({
          userId: idQuery.data?.guid,
        });
      }
      dispatch(setPreferredName(propOr('', 'first_name', idQuery.data)));
      dispatch(
        setName({
          first_name: propOr('', 'first_name', idQuery.data),
          last_name: propOr('', 'last_name', idQuery.data),
        })
      );
      dispatch(setUserSettings(settingsQuery.data));

      // Onboarding is complete only when the user has at least one real experience
      // entry (civilian job, JST military course, or JST military duty) AND has
      // saved location data with a zip code. Eligibility flags alone
      // (previous_military_service / current_military_service) do NOT count.
      const has_qualifying_experience =
        experienceQuery.isSuccess &&
        (experienceQuery.data?.job_experience?.length > 0 ||
          experienceQuery.data?.jst?.military_courses?.length > 0 ||
          experienceQuery.data?.jst?.military_experience?.length > 0);

      const has_location = locationQuery.isSuccess && !!locationQuery.data?.zip_code;

      if (experienceQuery.isSuccess) {
        dispatch(setExperienceForm(experienceQuery.data));
        dispatch(setExperienceHasResume(experienceQuery.data.has_resume));
      }

      if (has_qualifying_experience) {
        const currentSoc = pathOr('', ['data', 'job_experience', 0, 'soc'], experienceQuery);
        if (currentSoc !== '') {
          dispatch(setCurrentSoc(currentSoc));
          apiRequest('get', `/careers/${currentSoc}`).then((response) => {
            if (response?.title) {
              dispatch(setExperienceForm({ soc_code: response.soc, soc_title: response.title }));
            }
          });
        }
      }

      const onboarding_complete = has_qualifying_experience && has_location;

      // Check contact info first before setting onboarding step
      const onOnboardingPage = isOnOnboardingPage();

      if (!has_contact_info && !onOnboardingPage) {
        // Contact info not done → contact-info page
        dispatch(setNeedsContactInfo(true));
        dispatch(setNeedsOnboarding(true));
        dispatch(setOnboardingStep('contact-info'));
      } else if (!has_contact_info && onOnboardingPage) {
        dispatch(setNeedsContactInfo(false));
        dispatch(setNeedsOnboarding(true));
      } else if (!onboarding_complete) {
        // Contact info done but experience or location missing — resume at the
        // right step so the user picks up exactly where they left off.
        dispatch(setNeedsOnboarding(true));
        if (!onOnboardingPage) {
          if (!has_qualifying_experience) {
            dispatch(setOnboardingStep('eligibility'));
          } else {
            dispatch(setOnboardingStep('location'));
          }
        }
      } else if (onOnboardingPage) {
        // All steps complete but user is on an onboarding page (e.g. refreshed)
        dispatch(setNeedsOnboarding(true));
      }
      dispatch(setLoading(false));
    }

    // Handle experience query error (e.g., 500 error from backend)
    if (idQuery.isSuccess && experienceQuery.isError && settingsQuery.isSuccess && !experienceQuery.isLoading) {
      // eslint-disable-next-line no-console
      console.error('Experience API error, proceeding with empty experience data');

      const onOnboardingPage = isOnOnboardingPage();

      if (!has_contact_info && !onOnboardingPage) {
        dispatch(setNeedsContactInfo(true));
      } else if (onOnboardingPage) {
        dispatch(setNeedsContactInfo(false));
      }

      dispatch(setNeedsOnboarding(true));
      if (!onOnboardingPage) {
        dispatch(setOnboardingStep('eligibility'));
      }
      dispatch(setLoading(false));
    }

    if (idQuery.isError) {
      // Check if the error is due to the user not being found
      if (idQuery.error instanceof ApiError && idQuery.error?.message === 'User not found') {
        // Dispatch actions to set onboarding and contact info requirements
        const onOnboardingPage = isOnOnboardingPage();

        dispatch(setNeedsOnboarding(true));
        if (!onOnboardingPage) {
          dispatch(setNeedsContactInfo(true));
        } else {
          dispatch(setNeedsContactInfo(false));
        }
        dispatch(setLoading(false));
      } else {
        // Show a notification error for unexpected errors
        enqueueSnackbar(intl.formatMessage({ id: 'alerts.unexpectedError' }), {
          variant: 'error',
          autoHideDuration: null,
        });
        dispatch(setLoading(false));
      }
    }
  }, [
    idQuery.isSuccess,
    idQuery.isError,
    experienceQuery.isSuccess,
    experienceQuery.isError,
    experienceQuery.isLoading,
    settingsQuery.isSuccess,
    locationQuery.isSuccess,
    locationQuery.isError,
    worksearchLoaded,
  ]);

  useEffect(() => {
    if (!UI_COMPONENTS_UNDER_MAINTENANCE && idQuery.isSuccess) {
      apiRequest('GET', '/users/me/worksearch/')
        .then((res) => {
          if (res.status && res.status.worksearch_enabled) {
            dispatch(setActivities(res.activities));
            dispatch(setGuid(res.guid));
            dispatch(setStatus(res.status));
          } else if (res.status) {
            dispatch(setStatus(res.status));
          }
          dispatch(setLoaded(true));
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('Error loading worksearch data:', error);
          dispatch(setLoaded(true));
        });
    } else if (UI_COMPONENTS_UNDER_MAINTENANCE && idQuery.isSuccess) {
      // If UI components are under maintenance, mark as loaded
      dispatch(setLoaded(true));
    }
  }, [idQuery.isSuccess]);

  useEffect(() => {
    /**
     * Configuration of idle time before we track
     * the user as inactive
     */
    TimeMe.initialize({
      idleTimeoutInSeconds: 300, // seconds
    });
  }, []);

  useEffect(() => {
    if (idQuery.isSuccess) {
      TimeMe.callWhenUserLeaves(() => {
        const timeReport = TimeMe.getTimeOnAllPagesInSeconds();
        timeReport.forEach(({ pageName, timeOnPage }) => {
          if (pageName !== 'default-page-name') {
            trackEvent(`TIME_${pageName}_PAGETOTAL`, { timeOnPage: timeOnPage.toString() });
          }
        });
        const timeActiveSession = timeReport.reduce((prev, { timeOnPage }) => prev + timeOnPage, 0);
        trackEvent('TIME_ACTIVE_SESSION', { timeOnPage: timeActiveSession.toString() });
      });

      TimeMe.callWhenUserReturns(() => {
        TimeMe.resetAllRecordedPageTimes();
      });
    }
  }, [idQuery.isSuccess]);

  function render() {
    if (unrecoverableError) {
      return <ErrorMessage />;
    } else if (
      loading ||
      auth0_loading ||
      (isAuthenticated && !idQuery.isFetched) ||
      (isAuthenticated && idQuery.isSuccess && !needsOnboarding && !worksearchLoaded) ||
      (isAuthenticated && idQuery.isSuccess && !needsOnboarding && !needsContactInfo && experienceQuery.isLoading)
    ) {
      return <Loading />;
    } else if (!isAuthenticated) {
      return (
        <Switch>
          <Route path={['/login', '/:lang/login']} exact>
            <Login />
          </Route>
          <Route path={['/', '/:lang']}>
            <LaunchHP />
          </Route>
        </Switch>
      );
    } else if (needsContactInfo || needsOnboarding) {
      return <OnboardingPagesSwitch />;
    } else {
      return <AuthenticatedPagesSwitch />;
    }
  }

  return (
    <BrowserRouter>
      <ThemeProvider theme={theme}>
        <LocalizationProvider
          dateAdapter={AdapterDateFns}
          adapterLocale={intl.locale === 'en' ? undefined : dateFnsAdapterEsLocaleNoOptions}
          localeText={intl.locale === 'en' ? englishLocale : spanishLocale}
        >
          <Layout
            locale={intl.locale}
            loggedIn={isAuthenticated}
            signOut={signOut}
            serverUserLocale={experienceQuery?.data?.language}
          >
            <ScrollToTopOnPathChange />
            <Switch>
              <Route path={['/terms-of-use', '/:lang/terms-of-use']}>
                {intl.locale === 'en' ? <TermsOfUse /> : null}
                {intl.locale === 'es' ? <TermsOfUseES /> : null}
              </Route>
              <Route path={['/credits-and-citations', '/:lang/credits-and-citations']}>
                <CreditsCitations />
              </Route>
              <Route path={['/about', '/:lang/about']}>
                <About />
              </Route>
              <Route>{render()}</Route>
            </Switch>
          </Layout>
        </LocalizationProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
